// i18n
import { useTranslation } from 'react-i18next';

// react
import { MouseEvent, useContext, useState } from 'react';

// mui
import { Button, Grid, IconButton } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// types
import { ErrorCause, Feedback } from '../../../@types';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';

// icons
import { CloseOutlined as IconClose } from '@mui/icons-material';

// props
export interface FormAddDocTypeProps {
	onSuccess?: () => void;
}

export const FormAddDocType = ({ onSuccess }: FormAddDocTypeProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { userEntity } = useContext(ContextUserEntity);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState({
		name: '',
	});

	// vars
	const formId = 'form-add-document-type';

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${formId}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const { name } = entries;

		if (name) {
			try {
				// clear feedback
				setFeedback(null);

				// fetch doc types
				const body = {
					...entries,
					entity: userEntity?.id,
				};

				const fetchDocumentType = await fetch(`${process.env.REACT_APP_API_URL}/document-types`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(body),
				});
				const resAddDocumentType = await fetchDocumentType.json();

				if (resAddDocumentType.error) {
					throw new Error(resAddDocumentType.error.message, {
						cause: {
							id: resAddDocumentType.error.id,
						},
					});
				}

				if (onSuccess) {
					onSuccess();
				}

				// clear form
				setValues({
					name: '',
				});

				// set success banner
				setBanner({
					hasClose: true,
					message: t('alert.documentTypeCreated', { name }),
					severity: 'success',
				});
			} catch (error) {
				const err = error as Error;
				const cause = err.cause as ErrorCause;

				const feedbackNew: Feedback = {
					alert: {
						children: t('error.default'),
					},
				};

				if (cause.id === 'DOCUMENT_TYPE_DUPLICATE') {
					feedbackNew.alert.children = t('error.documentTypeDuplicate', { name });
				}

				if (cause.id === 'DOCUMENT_TYPE_NAME_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.documentTypeNameNonExistent');
				}

				if (cause.id === 'DOCUMENT_TYPE_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.documentTypeUnauthorized');
				}

				// set feedback
				setFeedback({
					...feedbackNew,
					alert: {
						...feedbackNew.alert,
						action: (
							<IconButton color="inherit" onClick={() => setFeedback(null)} size="small">
								<IconClose />
							</IconButton>
						),
						sx: {
							...feedbackNew.alert.sx,
							alignItems: 'center',
						},
					},
				});
			}
		}
	};

	return (
		<form id={formId} name="formAddDocumentType" onSubmit={handleSubmit}>
			<Grid container spacing={2} item xs={12}>
				<Grid item xs={12} md={true}>
					<Input
						id={`${formId}-name`}
						aria-label={t('addDocumentType')}
						fullWidth
						name="name"
						onChange={(e) => setValues((valuesOld) => ({ ...valuesOld, name: e.target.value }))}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md="auto">
					<Button fullWidth={true} type="submit" variant="outlined">
						{t('addDocumentType')}
					</Button>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
